import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Search, Plus, FolderOpen, Globe, Lock, Link as LinkIcon, Users, MoreVertical, Bookmark, Star, X, BrainCircuit, BookOpen, Clock, Filter, LayoutGrid, List, Library, Share2, Check, ArrowLeft, Trash2 } from "lucide-react";
import ShareMenu from "./library/ShareMenu";
import CreateQuizModal from "./library/CreateQuizModal";
import AIResultModal from "./pdf/AIResultModal";
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/pages/CategoriesPage.scss';

const CollectionCard = ({ collection, isDiscover = false, onSelect, onJoin, onToggleFavorite, onToggleSave, onDelete, isFavorite = false, isSaved = false }) => (
    <motion.div
        className="bg-card rounded-2xl border border-border/50 shadow-sm p-4 flex items-center gap-4 cursor-pointer hover:border-secondary/30 transition-colors hover:shadow-md"
        whileHover={{ y: -2 }}
        onClick={() => {
            if (!isDiscover && onSelect) {
                onSelect(collection);
            }
        }}
    >
        <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
            <FolderOpen className="w-5 h-5 text-secondary" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{collection.name}</p>
            {collection.description && <p className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5">{collection.description}</p>}
            <p className="text-[10px] text-muted-foreground flex items-center gap-2 mt-0.5">
                <span>{collection.pdf_ids?.length || collection.resourcesCount || 0} resources</span>
                <span>·</span>
                <span className="flex items-center gap-0.5">
                    {collection.visibility === 'public' ? <Globe className="w-3 h-3 text-primary" /> : (collection.visibility === 'private' ? <Lock className="w-3 h-3 text-muted-foreground" /> : <LinkIcon className="w-3 h-3 text-chart-4" />)}
                    {collection.visibility.charAt(0).toUpperCase() + collection.visibility.slice(1)}
                </span>
                {isDiscover && (
                    <>
                        <span>·</span>
                        <span className="flex items-center gap-0.5"><Users className="w-3 h-3" /> {collection.members || 1}</span>
                        <span>·</span>
                        <span>by {collection.owner || 'You'}</span>
                    </>
                )}
            </p>
        </div>
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <button 
                className={`w-8 h-8 rounded-xl transition-all flex items-center justify-center border ${
                    isFavorite ? 'bg-amber-500 text-amber-950 border-amber-500 shadow-md shadow-amber-500/20' : 'bg-card text-muted-foreground border-border/50 hover:text-foreground hover:border-border'
                }`}
                title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
                onClick={(e) => {
                    e.stopPropagation();
                    if (onToggleFavorite) {
                        onToggleFavorite(collection.id || collection._id);
                    }
                }}
            >
                <Star className={`w-3.5 h-3.5 ${isFavorite ? 'fill-current' : ''}`} />
            </button>
            <button 
                className={`w-8 h-8 rounded-xl transition-all flex items-center justify-center border ${
                    isSaved ? 'bg-accent text-accent-foreground border-accent shadow-md shadow-accent/20' : 'bg-card text-muted-foreground border-border/50 hover:text-foreground hover:border-border'
                }`}
                title={isSaved ? "Remove Bookmark" : "Save Category"}
                onClick={(e) => {
                    e.stopPropagation();
                    if (onToggleSave) {
                        onToggleSave(collection.id || collection._id);
                    }
                }}
            >
                <Bookmark className={`w-3.5 h-3.5 ${isSaved ? 'fill-current' : ''}`} />
            </button>
            {!isDiscover && onDelete && (
                <button
                    className="w-8 h-8 rounded-xl bg-card text-muted-foreground border border-border/50 hover:text-destructive hover:border-red-200 hover:bg-destructive/10 flex items-center justify-center transition-colors shadow-sm"
                    title="Delete Category"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(collection.id || collection._id);
                    }}
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            )}
            {isDiscover && (
                collection.joined ? (
                    <button disabled className="h-8 px-3 rounded-lg bg-primary/15 text-primary text-[10px] font-semibold transition-colors flex items-center gap-1 cursor-default opacity-80">
                        <Check className="w-3 h-3" /> Joined
                    </button>
                ) : (
                    <button
                        className="h-8 px-3 rounded-lg bg-primary text-primary-foreground text-[10px] font-semibold hover:bg-primary/90 transition-colors flex items-center gap-1"
                        onClick={(e) => { e.stopPropagation(); onJoin(collection.id || collection._id); }}
                    >
                        <Plus className="w-3 h-3" /> Join
                    </button>
                )
            )}
        </div>
    </motion.div>
);

const AddResourcesModal = ({ setShowAddModal, userMaterials, selectedCollection, user, handleAddResource }) => {
    const [modalSearch, setModalSearch] = useState('');
    
    // De-duplicate allAvailable array by item id
    const allAvailable = [];
    const seenIds = new Set();
    [...(userMaterials.uploaded || []), ...(userMaterials.saved || [])].forEach(m => {
        if (m && m.id && !seenIds.has(m.id)) {
            seenIds.add(m.id);
            allAvailable.push(m);
        }
    });

    const filtered = allAvailable.filter(m => {
        const titleMatch = (m.title || m.name || '').toLowerCase().includes(modalSearch.toLowerCase());
        // Type-safe string comparison to prevent ID format mismatch duplicates
        const notIncluded = !(selectedCollection.pdf_ids || []).map(String).includes(String(m.id));
        return titleMatch && notIncluded;
    });

    return (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
            <motion.div
                className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden"
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                onClick={e => e.stopPropagation()}
            >
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-display font-bold text-foreground">Add Resources</h3>
                        <p className="text-[10px] text-muted-foreground">Search and add books, notes, quizzes, exercises</p>
                    </div>
                    <button className="w-8 h-8 rounded-xl bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" onClick={() => setShowAddModal(false)}>
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-4 border-b border-border">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                            placeholder="Search resources..."
                            value={modalSearch} onChange={(e) => setModalSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                    </div>
                </div>
                <div className="overflow-y-auto max-h-[50vh] p-4 space-y-2 custom-scrollbar">
                    {filtered.length > 0 ? filtered.map((item) => (
                        <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold bg-primary/10 text-primary">
                                {(item.title || item.name || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-foreground truncate">{item.title || item.name}</p>
                                <p className="text-[10px] text-muted-foreground">{item.category || 'NOTE'} · {item.author || user?.firstname || 'You'}</p>
                            </div>
                            <button className="h-7 px-3 rounded-lg bg-primary text-primary-foreground text-[10px] font-semibold hover:bg-primary/90 transition-colors flex items-center gap-1" onClick={() => handleAddResource(item.id)}>
                                <Plus className="w-3 h-3" /> Add
                            </button>
                        </div>
                    )) : (
                        <p className="text-center text-xs text-muted-foreground py-8">No matching resources found.</p>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

const CategoriesPage = ({ user, token }) => {
    const [shareMenuId, setShareMenuId] = useState(null);
    const activeMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (activeMenuRef.current && !activeMenuRef.current.contains(e.target)) {
                setShareMenuId(null);
            }
        };
        if (shareMenuId) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [shareMenuId]);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    const handleDeleteCollection = async (collectionId) => {
        setErrorMsg(null);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL|| ""}/api/folders/${collectionId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                if (selectedCollection?.id === collectionId) {
                    setViewMode('list');
                    setSelectedCollection(null);
                }
                fetchData();
            } else {
                const data = await res.json();
                setErrorMsg(`Failed to delete collection: ${data.message || 'Unknown error'}`);
            }
        } catch (err) {
            console.error(err);
            setErrorMsg("Network error when deleting collection.");
        } finally {
            setShowDeleteConfirm(null);
        }
    };

    const location = useLocation();
    const isAddPath = location.pathname.endsWith('/add-category');
    const [activeTab, setActiveTab] = useState('my-collections');
    const [viewMode, setViewMode] = useState(isAddPath ? 'create' : 'list'); // 'list', 'create', 'detail'
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [quizResourceId, setQuizResourceId] = useState(null);
    const [quizResult, setQuizResult] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [collections, setCollections] = useState([]);
    const [discoverCollections, setDiscoverCollections] = useState([]);
    const [userMaterials, setUserMaterials] = useState({ uploaded: [], saved: [] });
    const [selectedCollectionMaterials, setSelectedCollectionMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [favoriteFolderIds, setFavoriteFolderIds] = useState([]);
    const [savedFolderIds, setSavedFolderIds] = useState([]);
    const navigate = useNavigate();
    const { lang } = useParams();
    const [searchParams] = useSearchParams();
    const categoryId = searchParams.get('id');

    // Dynamically adjust view mode on URL navigation changes
    useEffect(() => {
        if (location.pathname.endsWith('/add-category')) {
            setViewMode('create');
        } else if (location.pathname.endsWith('/categories')) {
            if (!searchParams.get('id')) {
                setViewMode('list');
            }
        }
    }, [location.pathname, searchParams]);

    // Fetch fresh user data (including bookmark IDs) directly from the server on mount
    useEffect(() => {
        const fetchUserData = async () => {
            if (!token) return;
            try {
                const res = await fetch('/api/auth/me', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setFavoriteFolderIds(data.user?.favorite_folder_ids || []);
                    setSavedFolderIds(data.user?.saved_folder_ids || []);
                }
            } catch (err) {
                console.error('Failed to fetch user bookmark data:', err);
            }
        };
        fetchUserData();
    }, [token]);

    const handleToggleFavoriteFolder = async (collectionId) => {
        // Optimistic UI update
        const wasFavorite = favoriteFolderIds.includes(collectionId);
        if (wasFavorite) {
            setFavoriteFolderIds(prev => prev.filter(id => id !== collectionId));
        } else {
            setFavoriteFolderIds(prev => [...prev, collectionId]);
        }

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL|| ""}/api/folders/${collectionId}/toggle-favorite`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                // Revert if failed
                if (wasFavorite) {
                    setFavoriteFolderIds(prev => [...prev, collectionId]);
                } else {
                    setFavoriteFolderIds(prev => prev.filter(id => id !== collectionId));
                }
                const data = await res.json();
                setErrorMsg(data.message || "Failed to update favorites.");
            } else {
                const data = await res.json();
                if (data.favorite_folder_ids) {
                    setFavoriteFolderIds(data.favorite_folder_ids);
                }
            }
        } catch (err) {
            console.error(err);
            // Revert if failed
            if (wasFavorite) {
                setFavoriteFolderIds(prev => [...prev, collectionId]);
            } else {
                setFavoriteFolderIds(prev => prev.filter(id => id !== collectionId));
            }
            setErrorMsg("Network error updating favorites.");
        }
    };

    const handleToggleSaveFolder = async (collectionId) => {
        // Optimistic UI update
        const wasSaved = savedFolderIds.includes(collectionId);
        if (wasSaved) {
            setSavedFolderIds(prev => prev.filter(id => id !== collectionId));
        } else {
            setSavedFolderIds(prev => [...prev, collectionId]);
        }

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL|| ""}/api/folders/${collectionId}/toggle-save`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                // Revert if failed
                if (wasSaved) {
                    setSavedFolderIds(prev => [...prev, collectionId]);
                } else {
                    setSavedFolderIds(prev => prev.filter(id => id !== collectionId));
                }
                const data = await res.json();
                setErrorMsg(data.message || "Failed to update saved collections.");
            } else {
                const data = await res.json();
                if (data.saved_folder_ids) {
                    setSavedFolderIds(data.saved_folder_ids);
                }
            }
        } catch (err) {
            console.error(err);
            // Revert if failed
            if (wasSaved) {
                setSavedFolderIds(prev => [...prev, collectionId]);
            } else {
                setSavedFolderIds(prev => prev.filter(id => id !== collectionId));
            }
            setErrorMsg("Network error updating saved collections.");
        }
    };
    
    // Create form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        visibility: 'public'
    });

    useEffect(() => {
        fetchData();
        fetchUserMaterials();
    }, [token]);

    const fetchData = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const res = await fetch('/api/folders', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCollections(data);
            }
            const resPublic = await fetch('/api/folders/public', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (resPublic.ok) {
                const publicData = await resPublic.json();
                setDiscoverCollections((publicData.folders || []).map(c => ({
                    ...c,
                    resourcesCount: c.itemCount ?? (c.pdf_ids?.length || 0),
                    owner: c.ownerName || 'Community Member',
                })));
            }
        } catch (err) {
            console.error(err);
            setErrorMsg("Failed to load collections. Please check your connection.");
        } finally {
            setLoading(false);
        }
    };

    const fetchUserMaterials = async () => {
        try {
            const res = await fetch('/api/materials/my-books', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setUserMaterials(data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchCollectionMaterials = async (ids) => {
        if (!ids || ids.length === 0) {
            setSelectedCollectionMaterials([]);
            return;
        }
        setErrorMsg(null);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL|| ""}/api/materials/by-ids?ids=${ids.join(',')}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedCollectionMaterials(data);
            } else {
                setErrorMsg("Failed to fetch collection materials.");
            }
        } catch (err) {
            console.error(err);
            setErrorMsg("Network error loading materials.");
        }
    };

    useEffect(() => {
        if (selectedCollection && viewMode === 'detail') {
            fetchCollectionMaterials(selectedCollection.pdf_ids);
        }
    }, [selectedCollection, viewMode]);

    useEffect(() => {
        if (!categoryId || loading) return;

        const allCollections = [...collections, ...discoverCollections];
        const cat = allCollections.find(c => String(c.id) === String(categoryId));
        if (cat) {
            const isOwned = collections.some(c => String(c.id) === String(categoryId));
            setSelectedCollection({ ...cat, isOwner: isOwned });
            setViewMode('detail');
            return;
        }

        // Not one of ours and not in the public Discover feed — this is likely
        // a direct "unlisted" (link-only) share, so fetch it by id directly.
        // A private collection that isn't ours will 404 here, which is correct.
        (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/folders/${categoryId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setSelectedCollection(data);
                    setViewMode('detail');
                } else {
                    setErrorMsg("This collection is private or no longer exists.");
                }
            } catch (err) {
                console.error(err);
                setErrorMsg("Network error loading collection.");
            }
        })();
    }, [categoryId, collections, discoverCollections, loading, token]);

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg(null);
        try {
            const res = await fetch('/api/folders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ ...formData, icon: 'Folder' })
            });
            if (res.ok) {
                fetchData();
                navigate(`/${lang || 'en'}/categories`);
                setFormData({ name: '', description: '', visibility: 'public' });
            } else {
                const data = await res.json();
                setErrorMsg(`Error saving category: ${data.error || data.message || 'Unknown error'}`);
                console.error("Backend Error:", data);
            }
        } catch (err) {
            console.error(err);
            setErrorMsg(`Network or Server Error: ${err.message}`);
        }
    };

    const handleAddResource = async (pdfId) => {
        setErrorMsg(null);
        try {
            const res = await fetch('/api/folders/add', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ name: selectedCollection.name, pdfIds: [pdfId] })
            });
            if (res.ok) {
                const updated = await res.json();
                setSelectedCollection(updated);
                setCollections(prev => prev.map(c => c.id === updated.id ? updated : c));
            } else {
                const data = await res.json();
                setErrorMsg(`Failed to add resource: ${data.error || data.message || 'Unknown error'}`);
            }
        } catch (err) {
            console.error(err);
            setErrorMsg("Network error when adding resource.");
        }
    };

    const handleRemoveResource = async (pdfId) => {
        setErrorMsg(null);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL|| ""}/api/folders/${selectedCollection.id}/remove`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ pdfId })
            });
            if (res.ok) {
                const updated = await res.json();
                setSelectedCollection(updated);
                setCollections(prev => prev.map(c => c.id === updated.id ? updated : c));
            } else {
                 const data = await res.json();
                 setErrorMsg(`Failed to remove resource: ${data.error || data.message || 'Unknown error'}`);
            }
        } catch (err) {
            console.error(err);
            setErrorMsg("Network error when removing resource.");
        }
    };

    const handleToggleSave = async (pdfId) => {
        // Optimistic UI Update
        const isCurrentlySaved = userMaterials.saved?.some(s => s.id === pdfId);
        
        setUserMaterials(prev => {
            if (isCurrentlySaved) {
                return { ...prev, saved: prev.saved.filter(s => s.id !== pdfId) };
            } else {
                const resourceToSave = selectedCollectionMaterials.find(m => m.id === pdfId) || userMaterials.uploaded.find(m => m.id === pdfId);
                return { ...prev, saved: prev.saved ? [...prev.saved, resourceToSave] : [resourceToSave] };
            }
        });

        try {
            const res = await fetch('/api/materials/toggle-save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ pdfId })
            });
            if (res.ok) {
                // Refresh saved materials locally
                fetchUserMaterials();
            } else {
                const data = await res.json();
                setErrorMsg(`Failed to save: ${data.error || 'Unknown error'}`);
            }
        } catch (err) {
            console.error(err);
            setErrorMsg("Network error when saving resource.");
        }
    };

    const handleJoinCollection = async (collectionId) => {
        setErrorMsg(null);
        // Optimistic UI Update: Mark it as joined immediately to prevent double-clicks
        setDiscoverCollections(prev => prev.map(c => {
            if (c && String(c.id || c._id) === String(collectionId)) {
                return { ...c, joined: true };
            }
            return c;
        }));
        setSelectedCollection(prev => (prev && String(prev.id) === String(collectionId)) ? { ...prev, joined: true } : prev);

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL|| ""}/api/folders/join`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ folderId: collectionId })
            });
            if (res.ok) {
                // Refresh full data to sync backend state
                fetchData();
            } else if (res.status === 409) {
                // Already joined — keep joined state, no revert needed
                // Just refresh to ensure UI is in sync
                fetchData();
            } else {
                // Revert optimistic update if request failed
                setDiscoverCollections(prev => prev.map(c => {
                    if (c && String(c.id || c._id) === String(collectionId)) {
                        return { ...c, joined: false };
                    }
                    return c;
                }));
                setSelectedCollection(prev => (prev && String(prev.id) === String(collectionId)) ? { ...prev, joined: false } : prev);
                const data = await res.json();
                setErrorMsg(`Failed to join collection: ${data.error || data.message || 'Unknown error'}`);
            }
        } catch (err) {
            // Revert optimistic update if network error occurred
            setDiscoverCollections(prev => prev.map(c => {
                if (c && String(c.id || c._id) === String(collectionId)) {
                    return { ...c, joined: false };
                }
                return c;
            }));
            setSelectedCollection(prev => (prev && String(prev.id) === String(collectionId)) ? { ...prev, joined: false } : prev);
            console.error(err);
            setErrorMsg("Network error when joining collection.");
        }
    };





    return (
        <div className="categories-page-container min-h-[calc(100vh-80px)] bg-background relative">
            {/* Premium Background Elements matching Library for light theme continuity */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[20%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/[0.03] blur-[120px]" />
                <div className="absolute bottom-[20%] right-[-5%] w-[30%] h-[30%] rounded-full bg-secondary/[0.03] blur-[100px]" />
            </div>

            <div className="max-w-[1440px] mx-auto px-6 pt-6 pb-12 relative z-10">
            {/* Header section */}
            {errorMsg && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-xl flex items-center justify-between">
                    <span>{errorMsg}</span>
                    <button onClick={() => setErrorMsg(null)}><X className="w-4 h-4" /></button>
                </div>
            )}
            <div className="flex items-center gap-3 mb-6">
                {(viewMode === 'detail' || viewMode === 'create') && (
                    <button className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors" onClick={() => setViewMode('list')}><ArrowLeft className="w-4 h-4" /></button>
                )}
                <div className="flex-1 min-w-0">
                    {viewMode === 'detail' ? (
                        <>
                            <div className="flex items-center gap-2">
                                <h1 className="text-[28px] font-display font-bold text-foreground truncate">{selectedCollection?.name}</h1>
                                {(() => { 
                                    const visibility = selectedCollection?.visibility || 'public';
                                    return visibility === 'public' ? <Globe className="w-4 h-4 text-primary" /> : (visibility === 'private' ? <Lock className="w-4 h-4 text-muted-foreground" /> : <LinkIcon className="w-4 h-4 text-chart-4" />); 
                                })()}
                            </div>
                            <p className="text-sm text-muted-foreground">{selectedCollection?.description || 'Everything you need for this pack.'}</p>
                        </>
                    ) : (
                        <>
                            <h1 className="text-[28px] font-display font-bold text-foreground">Categories & Collections</h1>
                            <p className="text-sm text-muted-foreground">Browse, create and manage your resource collections</p>
                        </>
                    )}
                </div>
                {viewMode === 'detail' && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mr-4">
                        <Users className="w-3.5 h-3.5" /> {selectedCollection?.members || 1} members
                    </div>
                )}
                {(viewMode === 'list' || viewMode === 'create') && (
                    <button className="h-9 px-4 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex items-center gap-1.5" onClick={() => navigate(`/${lang}/add-category`)}>
                        <Plus className="w-3.5 h-3.5" /> New Category
                    </button>
                )}
                {viewMode === 'detail' && selectedCollection?.isOwner !== false && (
                    <button className="h-8 px-4 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex items-center gap-1.5" onClick={() => setShowAddModal(true)}>
                        <Plus className="w-3 h-3" /> Add
                    </button>
                )}
                {viewMode === 'detail' && selectedCollection?.isOwner === false && (
                    selectedCollection.joined ? (
                        <button disabled className="h-8 px-4 rounded-xl bg-primary/15 text-primary text-xs font-semibold flex items-center gap-1.5 cursor-default opacity-80">
                            <Check className="w-3.5 h-3.5" /> Joined
                        </button>
                    ) : (
                        <button className="h-8 px-4 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex items-center gap-1.5" onClick={() => handleJoinCollection(selectedCollection.id)}>
                            <Plus className="w-3.5 h-3.5" /> Join Collection
                        </button>
                    )
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div
                            className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-6 relative"
                            initial={{ opacity: 0, scale: 0.95 }} 
                            animate={{ opacity: 1, scale: 1 }} 
                            exit={{ opacity: 0, scale: 0.95 }}
                        >
                            <h3 className="text-lg font-display font-bold text-foreground mb-2">Remove Category?</h3>
                            <p className="text-sm text-muted-foreground mb-6">
                                Are you sure you want to remove this category? This action cannot be undone.
                            </p>
                            <div className="flex gap-3 justify-end">
                                <button 
                                    className="px-4 py-2 rounded-xl text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    onClick={() => setShowDeleteConfirm(null)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-destructive/10 text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors"
                                    onClick={() => handleDeleteCollection(showDeleteConfirm)}
                                >
                                    Remove
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {viewMode === 'detail' && (
                <div className="flex items-center gap-2 mb-6 text-xs text-muted-foreground">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[8px] font-bold">YO</div>
                    <span>by <span className="font-semibold text-foreground">You</span></span>
                    <span>· {selectedCollection?.pdf_ids?.length || 0} resources</span>
                </div>
            )}

            {(viewMode === 'list' || viewMode === 'create') && (
                <>
                    <div className="flex gap-1 mb-4 p-1 bg-muted rounded-xl w-fit">
                        <button 
                            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === 'my-collections' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            onClick={() => setActiveTab('my-collections')}
                        >
                            My Collections
                        </button>
                        <button 
                            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === 'discover' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            onClick={() => setActiveTab('discover')}
                        >
                            Discover
                        </button>
                    </div>

                    <div className="relative mb-4">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input 
                            placeholder="Search categories..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-11 pr-4 py-2.5 rounded-xl bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                    </div>
                </>
            )}

            {viewMode === 'list' && (() => {
                const displayedCollections = (activeTab === 'my-collections' ? collections : discoverCollections).filter(c => 
                    !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase())
                );
                return (
                    <div className="space-y-3 custom-scrollbar">
                        {loading ? (
                            <div className="text-center py-16 text-muted-foreground font-medium">Loading collections...</div>
                        ) : activeTab === 'my-collections' ? (
                            displayedCollections.length > 0 ? (
                                displayedCollections.map((col, index) => (
                                    <CollectionCard 
                                        key={col.id || col._id || index} 
                                        collection={col} 
                                        isFavorite={favoriteFolderIds.includes(col.id || col._id)}
                                        isSaved={savedFolderIds.includes(col.id || col._id)}
                                        onToggleFavorite={handleToggleFavoriteFolder}
                                        onToggleSave={handleToggleSaveFolder}
                                        onDelete={() => setShowDeleteConfirm(col.id || col._id)} 
                                        onSelect={(c) => { setSelectedCollection(c); setViewMode('detail'); }} 
                                    />
                                ))
                            ) : (
                                <div className="text-center py-16">
                                    <FolderOpen className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                                    <p className="text-sm text-muted-foreground">
                                        {searchQuery ? "No collections match your search." : "No collections yet. Create one to get started!"}
                                    </p>
                                </div>
                            )
                        ) : (
                            displayedCollections.length > 0 ? (
                                displayedCollections.map((col, index) => (
                                    <CollectionCard 
                                        key={col.id || col._id || index} 
                                        collection={col} 
                                        isDiscover={true}
                                        isFavorite={favoriteFolderIds.includes(col.id || col._id)}
                                        isSaved={savedFolderIds.includes(col.id || col._id)}
                                        onToggleFavorite={handleToggleFavoriteFolder}
                                        onToggleSave={handleToggleSaveFolder}
                                        onJoin={handleJoinCollection} 
                                        onSelect={(c) => { setSelectedCollection(c); setViewMode('detail'); }} 
                                    />
                                ))
                            ) : (
                                <div className="text-center py-16">
                                    <Search className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                                    <p className="text-sm text-muted-foreground">No public collections found.</p>
                                </div>
                            )
                        )}
                    </div>
                );
            })()}

            {viewMode === 'create' && (
                <motion.div 
                    className="mb-6 bg-card rounded-2xl border border-border/50 p-5 shadow-sm transition-all duration-200 hover:shadow-md !p-5 border-secondary/30"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h3 className="text-sm font-display font-bold text-foreground mb-4">Create New Category</h3>
                    <form onSubmit={handleCreateSubmit} className="space-y-4">
                        <div>
                            <label className="text-xs font-medium text-foreground mb-1.5 block">Name <span className="text-destructive">*</span></label>
                            <input 
                                required
                                placeholder="e.g. Physics Study Pack"
                                value={formData.name}
                                onChange={e => setFormData({...formData, name: e.target.value})}
                                className="w-full px-4 py-2.5 rounded-xl bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-foreground mb-1.5 block">Description</label>
                            <textarea 
                                placeholder="What's this category about? Help others understand what they'll find here..."
                                value={formData.description}
                                onChange={e => setFormData({...formData, description: e.target.value})}
                                rows={3}
                                className="w-full px-4 py-2.5 rounded-xl bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-foreground mb-2 block">Visibility</label>
                            <div className="flex gap-2">
                                {['public', 'unlisted', 'private'].map(v => (
                                    <button 
                                        type="button"
                                        key={v}
                                        onClick={() => setFormData({...formData, visibility: v})}
                                        className={`flex-1 p-3 rounded-xl border text-left transition-all ${
                                            formData.visibility === v ? 'border-secondary bg-secondary/5' : 'border-border hover:border-border/80'
                                        }`}
                                    >
                                        <div className={`mb-1 ${v === 'public' ? 'text-primary' : (v === 'private' ? 'text-muted-foreground' : 'text-chart-4')}`}>
                                            {v === 'public' ? <Globe className="w-4 h-4" /> : (v === 'private' ? <Lock className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />)}
                                        </div>
                                        <p className="text-xs font-semibold text-foreground">{v.charAt(0).toUpperCase() + v.slice(1)}</p>
                                        <p className="text-[10px] text-muted-foreground">{v === 'public' ? 'Everyone can see and use' : (v === 'private' ? 'Only you can see' : 'Only friends & shared links')}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end pt-1">
                            <button type="button" className="px-4 py-2 rounded-xl bg-muted text-xs text-muted-foreground hover:text-foreground transition-colors" onClick={() => navigate(`/${lang || 'en'}/categories`)}>Cancel</button>
                            <button type="submit" className="px-5 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" disabled={!formData.name}>Create Category</button>
                        </div>
                    </form>
                </motion.div>
            )}

            {viewMode === 'detail' && (
                <div>
                    {(selectedCollection?.pdf_ids?.length || 0) === 0 ? (
                        <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl">
                            <FolderOpen className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                            <h3 className="text-sm font-semibold text-foreground mb-1">This category is empty</h3>
                            <p className="text-xs text-muted-foreground mb-4">Add books, notes, exercises or quizzes to get started.</p>
                            {selectedCollection?.isOwner !== false && (
                                <button className="h-9 px-5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5" onClick={() => setShowAddModal(true)}>
                                    <Plus className="w-3.5 h-3.5" /> Add Resources
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 custom-scrollbar pb-10">
                            <AnimatePresence>
                                {selectedCollectionMaterials.map((item, i) => (
                                    <motion.div 
                                        key={item.id} className="bg-card rounded-2xl border border-border/50 p-5 shadow-sm transition-all duration-200 hover:shadow-md !p-0 overflow-hidden group relative"
                                        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                                        style={{ animationDelay: `${i * 30}ms` }}
                                    >
                                        {selectedCollection?.isOwner !== false && (
                                            <button className="absolute top-2 left-2 z-10 w-6 h-6 rounded-lg bg-destructive/90 text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => handleRemoveResource(item.id)} title="Remove from category">
                                                <X className="w-3 h-3" />
                                            </button>
                                        )}
                                        <div className="w-full h-20 flex items-center justify-center text-2xl font-display font-bold bg-primary/10 text-primary opacity-80 group-hover:opacity-100 transition-opacity">
                                            {(item.title || item.name || 'U').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="p-4">
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide bg-primary/10 text-primary">{item.category || 'NOTE'}</span>
                                                <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground ml-auto"><Star className="w-3 h-3 text-chart-4 fill-chart-4" /> {item.rating || '0.0'}</span>
                                            </div>
                                            <h4 className="text-sm font-display font-bold text-foreground hover:text-secondary transition-colors truncate">{item.title || item.name}</h4>
                                            <p className="text-xs text-muted-foreground line-clamp-2 mt-1 leading-relaxed">{item.description || 'No description available for this resource.'}</p>
                                            
                                            <div className="flex items-center gap-2 mt-2.5">
                                                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[8px] font-bold">
                                                    {(item.author || user?.firstname || 'U').charAt(0).toUpperCase()}
                                                </div>
                                                <span className="text-[10px] font-medium text-foreground">{item.author || user?.firstname || 'You'}</span>
                                                <span className="text-[10px] text-muted-foreground">· {item.university || 'Academic Institution'}</span>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[10px] text-muted-foreground">
                                                <span className="flex items-center gap-0.5"><BookOpen className="w-2.5 h-2.5" /> {item.pages || 0}p</span>
                                                <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> {item.readTime ? item.readTime : ((item.pages || 0) * 2)} mins</span>
                                                {(item.tags || [item.topic]).slice(0, 3).map(t => t && (
                                                    <span key={t} className="px-1.5 py-0.5 rounded-md bg-muted font-medium">{t}</span>
                                                ))}
                                            </div>

                                            <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-border/50">
                                                <button className="flex-1 h-8 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-1" onClick={() => window.open(`/${lang || 'en'}/material/${item.id}`, '_blank')}>
                                                    <BookOpen className="w-3 h-3" /> Open
                                                </button>
                                                <button 
                                                    className={`h-8 px-2.5 rounded-xl text-xs font-medium transition-colors flex items-center gap-1 border ${
                                                        userMaterials.saved?.some(s => s.id === item.id) 
                                                            ? 'bg-accent text-accent-foreground border-accent shadow-md shadow-accent/20' 
                                                            : 'bg-card text-muted-foreground border-border/50 hover:text-foreground hover:border-border'
                                                    }`}
                                                    onClick={() => handleToggleSave(item.id)}
                                                    title={userMaterials.saved?.some(s => s.id === item.id) ? "Remove Bookmark" : "Save Bookmark"}
                                                >
                                                    <Bookmark className={`w-3.5 h-3.5 ${userMaterials.saved?.some(s => s.id === item.id) ? 'fill-current' : ''}`} /> 
                                                    {userMaterials.saved?.some(s => s.id === item.id) ? 'Saved' : 'Save'}
                                                </button>
                                                <button 
                                                    className="h-8 w-8 rounded-xl bg-card text-muted-foreground border border-border/50 hover:text-accent hover:border-border flex items-center justify-center transition-colors shadow-sm" 
                                                    title="Create Quiz"
                                                    onClick={() => setQuizResourceId(item.id)}
                                                >
                                                    <BrainCircuit className="w-3.5 h-3.5" />
                                                </button>
                                                <div className="relative">
                                                    <button 
                                                        className={`h-8 w-8 rounded-xl flex items-center justify-center transition-colors shadow-sm border ${
                                                            shareMenuId === item.id ? 'bg-accent text-accent-foreground border-accent' : 'bg-card text-muted-foreground border-border/50 hover:text-accent hover:border-border'
                                                        }`} 
                                                        title="Share"
                                                        onClick={() => setShareMenuId(shareMenuId === item.id ? null : item.id)}
                                                    >
                                                        <Share2 className="w-3.5 h-3.5" />
                                                    </button>
                                                    {shareMenuId === item.id && (
                                                        <div ref={activeMenuRef} className="absolute bottom-full right-0 mb-2 z-[100] animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                            <ShareMenu
                                                                resourceId={item.id}
                                                                resourceTitle={item.title || item.name}
                                                                onClose={() => setShareMenuId(null)}
                                                                position="right"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            )}

            <AnimatePresence>
                {showAddModal && <AddResourcesModal 
                    setShowAddModal={setShowAddModal} 
                    userMaterials={userMaterials} 
                    selectedCollection={selectedCollection} 
                    user={user} 
                    handleAddResource={handleAddResource} 
                />}
                {quizResourceId && (
                    <CreateQuizModal
                        resourceId={quizResourceId}
                        resourceTitle={selectedCollectionMaterials.find(m => m.id === quizResourceId)?.title || ""}
                        token={token}
                        onClose={() => setQuizResourceId(null)}
                        onGenerated={(data) => { setQuizResourceId(null); setQuizResult(data); }}
                    />
                )}
                <AIResultModal
                    isOpen={!!quizResult}
                    onClose={() => setQuizResult(null)}
                    loading={false}
                    error={null}
                    title="Quiz"
                    textContent=""
                    questions={quizResult?.questions}
                    materialTitle={quizResult?.materialTitle}
                />
            </AnimatePresence>
            </div>
        </div>
    );
};

export default CategoriesPage;
